// Lexical Table の編集アクション集。
// editor.update 内で呼ぶ前提。 selection が table cell の中にあれば動作、
// それ以外は no-op (ボタンが押されただけで何も起きない)。

import {
  $getSelection, $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
} from "lexical"
import {
  $insertTableRowAtSelection,
  $insertTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $deleteTableColumnAtSelection,
  $getTableCellNodeFromLexicalNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableColumnIndexFromTableCellNode,
  $computeTableMap,
  $isTableCellNode,
  $isTableSelection,
  $mergeCells,
  $unmergeCell,
  TableCellHeaderStates,
} from "@lexical/table"

// 選択中の cell を取得 (TableSelection / RangeSelection の両方を受ける)
function $cellAtSelection() {
  const sel = $getSelection()
  if (!sel) return null
  let node = null
  if ($isRangeSelection(sel)) {
    node = sel.anchor.getNode()
  } else if (sel.anchorNode) {
    node = sel.anchorNode
  } else if (typeof sel.getNodes === "function") {
    node = sel.getNodes()[0]
  }
  if (!node) return null
  const cell = $getTableCellNodeFromLexicalNode(node)
  return $isTableCellNode(cell) ? cell : null
}

const wrap = (editor, fn) => editor.update(() => {
  if (!$cellAtSelection()) return  // テーブル内じゃなければ no-op
  fn()
})

// ─── 行 ──────────────────────────────────────────────
export const insertRowAbove = (editor) => wrap(editor, () => $insertTableRowAtSelection(false))
export const insertRowBelow = (editor) => wrap(editor, () => $insertTableRowAtSelection(true))
export const deleteRow      = (editor) => wrap(editor, () => $deleteTableRowAtSelection())

// ─── 列 ──────────────────────────────────────────────
export const insertColumnLeft  = (editor) => wrap(editor, () => $insertTableColumnAtSelection(false))
export const insertColumnRight = (editor) => wrap(editor, () => $insertTableColumnAtSelection(true))
export const deleteColumn      = (editor) => wrap(editor, () => $deleteTableColumnAtSelection())

// ヘッダー切替の共通実装: 対象 cell 群を取って、 「全部 header」 なら off、
// 一つでも off なら全部 on にする。 結合 cell も $computeTableMap 経由で
// 正しく辿るので、 重複処理しない。
function toggleHeaderState(editor, headerFlag, pickCells /* (tableMap, cell) → TableCellNode[] */) {
  wrap(editor, () => {
    const cell = $cellAtSelection()
    if (!cell) return
    const table = $getTableNodeFromLexicalNodeOrThrow(cell)
    const [tableMap] = $computeTableMap(table, cell, cell)
    if (!tableMap || tableMap.length === 0) return

    const cells = pickCells(tableMap, cell)
    if (cells.length === 0) return

    // 全 cell が header なら off、 一つでも off があれば全部 on
    const allOn = cells.every((c) => (c.getHeaderStyles() & headerFlag) !== 0)
    cells.forEach((c) => {
      const current = c.getHeaderStyles()
      c.setHeaderStyles(allOn ? (current & ~headerFlag) : (current | headerFlag))
    })
  })
}

// ─── ヘッダー行トグル ────────────────────────────────
// 最上段 (row 0) のすべての cell を <th> ↔ <td>
export const toggleHeaderRow = (editor) =>
  toggleHeaderState(editor, TableCellHeaderStates.ROW, (tableMap) => {
    const seen = new Set()
    const out = []
    for (const mapCell of tableMap[0]) {
      if (!mapCell) continue
      const c = mapCell.cell
      if (seen.has(c.getKey())) continue
      seen.add(c.getKey())
      out.push(c)
    }
    return out
  })

// ─── テーブル全削除 ─────────────────────────────────
export const deleteTable = (editor) => wrap(editor, () => {
  const cell = $cellAtSelection()
  if (!cell) return
  const table = $getTableNodeFromLexicalNodeOrThrow(cell)
  table.remove()
})

// ─── セル結合 / 解除 ────────────────────────────────
// 結合: 複数 cell 選択 (TableSelection) の時のみ動く
export const mergeCells = (editor) => editor.update(() => {
  const sel = $getSelection()
  if (!$isTableSelection(sel)) return
  const cells = sel.getNodes().filter($isTableCellNode)
  if (cells.length < 2) return
  $mergeCells(cells)
})

// 解除: 結合 cell (colSpan>1 か rowSpan>1) にカーソルがある時のみ
export const unmergeCells = (editor) => wrap(editor, () => {
  const cell = $cellAtSelection()
  if (!cell) return
  if (cell.getColSpan() <= 1 && cell.getRowSpan() <= 1) return
  $unmergeCell()
})

// ─── 列ヘッダー切替 ─────────────────────────────────
// 選択中 cell の列を辿り、 各行の同じ列の cell を <th> ↔ <td>
// (結合 cell があっても tableMap が grid 状なので正しく拾える)
export const toggleHeaderColumn = (editor) =>
  toggleHeaderState(editor, TableCellHeaderStates.COLUMN, (tableMap, cell) => {
    // 選択 cell の grid 列位置を求める
    let targetCol = -1
    outer: for (let r = 0; r < tableMap.length; r++) {
      for (let c = 0; c < tableMap[r].length; c++) {
        if (tableMap[r][c]?.cell === cell) { targetCol = c; break outer }
      }
    }
    if (targetCol < 0) return []
    const seen = new Set()
    const out = []
    for (let r = 0; r < tableMap.length; r++) {
      const mapCell = tableMap[r][targetCol]
      if (!mapCell) continue
      const c = mapCell.cell
      if (seen.has(c.getKey())) continue
      seen.add(c.getKey())
      out.push(c)
    }
    return out
  })

// ─── テキスト整列 ───────────────────────────────────
// FORMAT_ELEMENT_COMMAND は cell 内の paragraph の text-align を変える
export const alignLeft   = (editor) => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left")
export const alignCenter = (editor) => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")
export const alignRight  = (editor) => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right")

// ─── セル背景色 ────────────────────────────────────
// TableCellNode.setBackgroundColor(color) で着色。
// 選択中の全 cell に適用 (単一 / 複数 selection 両対応)
export const setCellBackground = (editor, color /* CSS string or null */) => editor.update(() => {
  const sel = $getSelection()
  if (!sel) return
  let cells = []
  if ($isTableSelection(sel)) {
    cells = sel.getNodes().filter($isTableCellNode)
  } else {
    const single = $cellAtSelection()
    if (single) cells = [single]
  }
  cells.forEach((c) => c.setBackgroundColor(color))
})

// パレットで使う色 (Tailwind 系のソフトトーン)
export const CELL_BG_COLORS = [
  { key: "clear",  label: "クリア",  value: null,       css: "transparent",
    border: "var(--color-akebono-border)" },
  { key: "red",    label: "赤",      value: "#fee2e2", css: "#fee2e2" },
  { key: "orange", label: "橙",      value: "#fed7aa", css: "#fed7aa" },
  { key: "yellow", label: "黄",      value: "#fef08a", css: "#fef08a" },
  { key: "green",  label: "緑",      value: "#bbf7d0", css: "#bbf7d0" },
  { key: "teal",   label: "ティール", value: "#99f6e4", css: "#99f6e4" },
  { key: "blue",   label: "青",      value: "#bfdbfe", css: "#bfdbfe" },
  { key: "indigo", label: "藍",      value: "#c7d2fe", css: "#c7d2fe" },
  { key: "purple", label: "紫",      value: "#e9d5ff", css: "#e9d5ff" },
  { key: "pink",   label: "桃",      value: "#fbcfe8", css: "#fbcfe8" },
  { key: "gray",   label: "灰",      value: "#e4e4e7", css: "#e4e4e7" },
]

// dropdown menu 用エントリ (block_commands.js と同じ形式)
export const TABLE_ACTION_GROUPS = [
  {
    label: "行",
    items: [
      { id: "row-above",  title: "行を上に追加",  desc: "現在の行の上に新しい行", icon: "↑+",
        aliases: ["row above", "上に行"],
        run: (editor) => insertRowAbove(editor) },
      { id: "row-below",  title: "行を下に追加",  desc: "現在の行の下に新しい行", icon: "↓+",
        aliases: ["row below", "下に行"],
        run: (editor) => insertRowBelow(editor) },
      { id: "row-delete", title: "この行を削除",  desc: "現在の行を消す", icon: "✕",
        aliases: ["row delete", "行削除"],
        run: (editor) => deleteRow(editor) },
    ],
  },
  {
    label: "列",
    items: [
      { id: "col-left",   title: "列を左に追加",  desc: "現在の列の左に新しい列", icon: "←+",
        aliases: ["column left", "左に列"],
        run: (editor) => insertColumnLeft(editor) },
      { id: "col-right",  title: "列を右に追加",  desc: "現在の列の右に新しい列", icon: "→+",
        aliases: ["column right", "右に列"],
        run: (editor) => insertColumnRight(editor) },
      { id: "col-delete", title: "この列を削除",  desc: "現在の列を消す", icon: "✕",
        aliases: ["column delete", "列削除"],
        run: (editor) => deleteColumn(editor) },
    ],
  },
  {
    label: "テーブル",
    items: [
      { id: "toggle-header", title: "ヘッダー行 切替", desc: "最初の行を <th> ↔ <td>", icon: "H",
        aliases: ["toggle header", "ヘッダー"],
        run: (editor) => toggleHeaderRow(editor) },
      { id: "table-delete",  title: "テーブル全体を削除", desc: "テーブルごと消す", icon: "🗑",
        aliases: ["table delete", "テーブル削除"],
        run: (editor) => deleteTable(editor) },
    ],
  },
]
